import dayjs from "dayjs";
import toast from "react-hot-toast";
import {v4 as uuidv4} from "uuid";
import {useHistory} from "react-router";
import {useDropzone} from "react-dropzone";
import {useContext, useEffect, useState} from "react";
import {useFieldArray, useForm, useWatch} from "react-hook-form";
import {IonCol, IonGrid, IonItemDivider, IonRow} from "@ionic/react";

// GraphQL
import {useLazyQuery, useMutation} from "@apollo/client";
import {
  CREATE_MY_ORDER_MUTATION,
  UPDATE_MY_ORDER_MUTATION,
} from "../../../graphql/mutations/services.mutation";
import {GET_MY_SINGLE_ORDER} from "../../../graphql/queries/myorder.query";
import {UPLOAD_IMAGE_MUTATION} from "../../../graphql/mutations/common.mutation";
import {GET_APPOINTMENT_BY_REFID} from "../../../graphql/queries/appointment.query";

// Context
import {AuthContext} from "../../../context/AuthContext";

// Custom components
import FileLink from "./FileLink";
import ChatIcon from "../../../components/ChatIcon";
import {
  APPOINTMENT_PATENTE_URL,
  APPOINTMENT_URL,
} from "../../../constants/appConfig";
import {
  Accordion,
  AppButton,
  DatePicker,
  Input,
  Select,
} from "../../../components";
import {sortClusters} from "../../../lib/utils/clusters.utils";
import {capitalizeString, getSignedUrl} from "../../../lib/utils/common.utils";

// TODO:: Fetch master order and display few fields value from the master field
// 1. canone annuo
// 2. expiration date for pec renewal service
const Orders = (props: IOrdersProps) => {
  const {user, removeServiceFromCart} = useContext(AuthContext);

  // url params
  const history = useHistory();
  const historySearch = history.location.search;
  const urlParams = new URLSearchParams(historySearch);

  // Opening chat through url params
  const uChat = urlParams.get("chat");
  const refId = urlParams.get("refid");

  // Master order/service related data
  const [serviceId, serviceType, masterId, appointmentId] = [
    urlParams.get("serviceId"),
    urlParams.get("serviceType"),
    urlParams.get("masterId"),
    urlParams.get("id"),
  ];

  const isSubService = serviceId && serviceType && masterId;

  // Appoinments loaded
  const [appointments, setAppointments] = useState<any[]>([]);
  const [refIds, setRefIds] = useState<any[]>([]);

  // Mutations
  const [uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION);
  const [createMyOrderMutation] = useMutation(CREATE_MY_ORDER_MUTATION);
  const [updateMyOrderMutation] = useMutation(UPDATE_MY_ORDER_MUTATION);

  const [getAppointmentByRefId] = useLazyQuery(GET_APPOINTMENT_BY_REFID, {
    fetchPolicy: "network-only",

    onCompleted(data) {
      console.log(data, "data");
      const appointmentData = data.getAppointmentByRefId.data;
      if (!appointmentData) return;

      setAppointments((prev) => [...prev, appointmentData]);
      setRefIds((prev) => [...prev, appointmentData]);
    },
  });

  // Master order
  const [masterOrder, setMasterOrder] = useState<any>(null);

  const [getMasterOrderDetails] = useLazyQuery(GET_MY_SINGLE_ORDER, {
    onCompleted(data) {
      const orderData = data.getMyOrderDetail.data;
      if (!orderData) return;

      setMasterOrder(orderData);
    },
  });

  const canBuyTerminateService = () => {
    if (serviceType !== "terminate") return true;
    if (!masterOrder) return false;

    const oldExpireDate = masterOrder.orderedService
      ?.map((os: any) => os?.fields)
      ?.flat()
      ?.find((fld: any) => fld?.data?.name?.toLowerCase() === "data di fine");

    if (!oldExpireDate?.data?.value) return false;
    if (dayjs(oldExpireDate.data.value).isBefore(new Date())) return false;

    return true;
  };

  // Visiblity states
  const [isChatActive, setIsChatActive] = useState(false);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);

  // File states
  const [fileFields, setFileFields] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  const inputFileFields = [...fileFields].filter(
    (field) => field.ioType === "input"
  );
  const outputFileFields = [...fileFields].filter(
    (field) => field.ioType === "output"
  );

  // react-dropzone
  const {getRootProps, getInputProps} = useDropzone({
    onDrop: async (acceptedFiles) => {
      // Do not update the existing file list if more files are added than possible count
      const fileFieldsLength = props.isOutputType
        ? fileFields.length
        : inputFileFields.length;

      if (uploadedFiles.length + acceptedFiles.length > fileFieldsLength)
        return;

      const uploadedFilesNew = await Promise.all(
        acceptedFiles
          .filter(
            (file: any) => !uploadedFiles.map((f) => f.path).includes(file.path)
          )
          .map((file: any) => {
            return uploadImage({
              variables: {
                file,
              },
            });
          })
      );

      acceptedFiles.forEach((file, index) => {
        Object.assign(file, {
          preview: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
          linkedField: null,
          imageUrl: uploadedFilesNew[index].data.uploadFile,
          originalUrl: uploadedFilesNew[index].data.uploadFile,
        });

        setUploadedFiles((prevState) => [...prevState, file]);
      });

      setIsFileModalOpen(true);
    },
  });

  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    control,
    formState: {errors},
  } = useForm();

  const formProps = {
    handleSubmit,
    register,
    getValues,
    setValue,
    control,
    errors,
  };

  const formValues = useWatch({
    control,
    name: "orderedServices",
  });

  const {fields} = useFieldArray({
    control,
    name: "orderedServices",
  });

  /* Before submission ?? */
  const _getFieldStructure = (
    field: any,
    name: string,
    serviceIndex: number,
    clusterIndex: number,
    itemIndex?: number,
    fieldIndex?: number
  ) => {
    let fieldFormName = `documents.orderedServices-${serviceIndex}-${name}-${clusterIndex}`;
    if (itemIndex?.toString() && fieldIndex?.toString())
      fieldFormName += `-items-${itemIndex}-fields-${fieldIndex}`;

    const allFiles = [...uploadedFiles];

    const linkedDoc =
      allFiles.find((file) => file.linkedField === fieldFormName)
        ?.originalUrl ?? null;

    return {
      fieldId: field.id,
      documents: field.type === "file" && linkedDoc ? [linkedDoc] : [],
      cluster: field.cluster ?? null,
      data: {
        name: field.name,
        value: field.type !== "file" ? field.value : null,
      },
    };
  };

  const formatOutputDataFromFields = (
    data: any,
    orderedServiceIndex: number
  ) => {
    return [
      ...data.inputFields.map((field: any, i: number) =>
        _getFieldStructure(field, "inputFields", orderedServiceIndex, i)
      ),
      ...data.outputFields.map((field: any, i: number) =>
        _getFieldStructure(field, "outputFields", orderedServiceIndex, i)
      ),
      ...data.inputFieldsClusters
        .map((entry: any, i: number) =>
          entry.items.map((item: any, j: number) =>
            item.fields.map((field: any, k: number) =>
              _getFieldStructure(
                field,
                "inputFieldsClusters",
                orderedServiceIndex,
                i,
                j,
                k
              )
            )
          )
        )
        .flat(3),
      ...data.outputFieldsClusters
        .map((entry: any, i: number) =>
          entry.items.map((item: any, j: number) =>
            item.fields.map((field: any, k: number) =>
              _getFieldStructure(
                field,
                "outputFieldsClusters",
                orderedServiceIndex,
                i,
                j,
                k
              )
            )
          )
        )
        .flat(3),
    ];
  };

  const submitOrderData = async (data: any) => {
    // Checking if the terminate service can be bought or not
    const canBuy = canBuyTerminateService();
    if (!canBuy) {
      toast.error(
        "Per acquistare il servizio è necessario indicare la data di fine"
      );
      return;
    }

    // Checking if the uploaded files are linked or not
    const uploadedFilesCount = uploadedFiles.length;
    const usedFilesCount = uploadedFiles.filter(
      (file) => file.linkedField
    ).length;

    const minFileCountRequired = Math.min(
      uploadedFilesCount,
      fileFields.length
    );
    if (uploadedFilesCount && usedFilesCount < minFileCountRequired) {
      toast.error(
        "Per proseguire è necessario associare i documenti caricati con le informazioni richieste"
      );
      return;
    }

    // Formatting srevice data
    const serviceData = data.orderedServices.map((serv: any, index: number) => {
      const fields = formatOutputDataFromFields(serv, index);

      console.log(fields, "fields service data");

      //
      const evasionDate =
        fields.find(
          (field: any) => field.data.name === "richiesta di evasione entro il"
        )?.data?.value ?? null;

      //
      const expirationDate =
        fields.find((field: any) => field.data.name === "Data Scadenza")?.data
          ?.value ?? null;

      //

      return {
        ...(serv.id ? {id: serv.id} : {}),
        serviceId: serv.service.id,
        fields,
        evasionDate,
        expirationDate,
      };
    });

    // Createing new order if it is one of:
    // 1. Not Existing order
    // 2. sub service type order
    if (!props.isOutputType || isSubService) {
      if (props.handleGoToNextPage) {
        console.log("this exists so im here");
        props.handleGoToNextPage({
          agencyId: user?.agencyId,
          customerId: user?.id,
          services: serviceData,
          accountHolder: data.accountHolder ?? "",
          ...(masterId ? {masterId: parseInt(masterId)} : {}),
        });
        return;
      } else {
        const resp = await createMyOrderMutation({
          variables: {
            agencyId: user?.agencyId,
            customerId: user?.id,
            services: serviceData,
            accountHolder: data.accountHolder ?? "",
            ...(masterId ? {masterId: parseInt(masterId)} : {}),
          },
        });

        const respData = resp.data.createMyOrder;
        if (respData?.errors) {
          toast.error(respData?.errors[0]?.message);
          return;
        }

        // Redirect to checkout page and exit without removing items from cart if it is a subservice
        if (isSubService) {
          history.push(
            `/personale/carrello?orderId=${respData?.data?.id}&masterId=${masterId}`
          );
          return;
        }

        // Removing the services from cart
        for (const cartItem of props.data) {
          removeServiceFromCart(cartItem.id);
        }

        // Progressing to checkout page
        history.push("/personale/carrello");
      }
    }
    // Updating existing order
    else {
      const orderData = props.orderData;

      const billingAddress = {...orderData.billingAddress};
      delete billingAddress.__typename;

      const resp = await updateMyOrderMutation({
        variables: {
          agencyId: user?.agencyId,
          services: serviceData,
          billingAddress: billingAddress,
          id: orderData.id,
          billTo: orderData.billTo,
          accountHolder: data.accountHolder,
        },
      });

      const respData = resp.data.updateMyOrder;
      if (respData?.errors) {
        toast.error(respData?.errors[0]?.message);
        return;
      }

      toast.success("Pratiche aggiornato correttamente");
      history.push("/personale/servizi");
    }
  };

  /* Helper functions */
  const getFormattedClusters = (fields: any[], service: any) => {
    const clusterFields = [];
    const lookedUpClusters: string[] = [];

    for (const field of fields.filter((fld: any) => fld.cluster)) {
      const clusterValue = service.clusters.find(
        (clust: any) => clust.name === field.cluster
      );

      if (!lookedUpClusters.includes(field.cluster)) {
        clusterFields.push({
          name: field.cluster,
          isRepeatable: clusterValue?.isRepeatable,
          items: [{fields: [field]}],
        });
      } else {
        const clusterItem =
          clusterFields.find((ic) => ic.name === field.cluster)?.items ?? [];
        const lastItemInCluster = clusterItem[clusterItem.length - 1];
        if (lastItemInCluster.fields.map((fld) => fld.id).includes(field.id)) {
          clusterItem.push({fields: [field]});
        } else {
          lastItemInCluster.fields.push(field);
        }
      }

      if (!lookedUpClusters.includes(field.cluster))
        lookedUpClusters.push(field.cluster);
    }

    // Adding every field in every cluster
    clusterFields.forEach((cluster: any) => {
      const uniqueFields: number[] = [];

      cluster.items.forEach((item: any) => {
        item.fields.forEach((field: any) => {
          if (!uniqueFields.includes(field.id)) {
            uniqueFields.push(field.id);
          }
        });
      });

      for (const uField of uniqueFields) {
        cluster.items.forEach((item: any) => {
          if (!item.fields.map((field: any) => field.id).includes(uField)) {
            item.fields.push(fields.find((fld: any) => fld.id === uField));
          }
        });
      }
    });

    // sorting fields
    clusterFields.forEach((cluster: any) => {
      cluster.items.forEach((item: any) => {
        item.fields.sort((a: any, b: any) => a.id - b.id);
      });
    });

    //
    return sortClusters(clusterFields);
  };

  const getFieldsForOldOrder = (fields: any[], service: any) => {
    const _fields = fields;
    const serviceFields = service.fields;

    const mappedFields = _fields.map((field: any) => {
      const serviceFld = serviceFields.find(
        (fld: any) => fld.id === field.fieldId
      );

      return {
        ...serviceFld,
        value: field.data?.value,
        documents: field.documents,
      };
    });

    /**
     * I initially though that every order should have every field, even if the actual service is changed afterwards
     * But this seems not to be the case. So commenting out the codes below.
     *
     * However if this is necessary in the future, we can uncomment them.
     */

    // for (const field of serviceFields) {
    //   if (!mappedFields.find((fld: any) => fld.id === field.id)) {
    //     mappedFields.push({
    //       ...field,
    //       value: null,
    //     });
    //   }
    // }

    return mappedFields;
  };

  const formatFieldsIntoObjects = (_fields: any[], service: any) => {
    const fields =
      _fields?.map((field: any) => ({
        ...field,
        value: field.value ?? null,
      })) ?? [];

    const inputFieldsRaw = fields.filter(
      (field: any) => field.ioType === "input"
    );
    const outputFieldsRaw = fields.filter(
      (field: any) => field.ioType === "output"
    );

    const inputFieldsClusters = getFormattedClusters(inputFieldsRaw, service);
    const inputFields = inputFieldsRaw
      .filter((field: any) => !field.cluster)
      .sort((a, b) => a.id - b.id);

    const outputFieldsClusters = getFormattedClusters(outputFieldsRaw, service);
    const outputFields = outputFieldsRaw
      .filter((field: any) => !field.cluster)
      .sort((a, b) => a.id - b.id);

    return {
      inputFields,
      inputFieldsClusters,
      outputFields,
      outputFieldsClusters,
    };
  };

  /* Appointment helper function */
  const redirectToAppointmentPage = (
    fieldName: string,
    cartItemId: number,
    appointmentType: "appointment" | "appointmentPatente"
  ) => {
    const newAppointmentId = uuidv4();

    const redirectUrl =
      appointmentType === "appointmentPatente"
        ? APPOINTMENT_PATENTE_URL
        : APPOINTMENT_URL;
    setValue(fieldName, newAppointmentId);
    // console.log(fieldName, "fieldName");

    const dataToStore = formValues.map((serv: any, index: number) => ({
      id: serv.id,
      serviceId: serv.service.id,
      fields: formatOutputDataFromFields(serv, index),
      appointmentUuid:
        serv.id === cartItemId ? newAppointmentId : serv.value || null,
    }));

    localStorage.setItem(newAppointmentId, JSON.stringify(dataToStore));

    // Redirect to take the appointment
    if (props.isOutputType) {
      window.location.href = `${redirectUrl}?redirect=${window.location.origin}/personale/add-to-cart/${props.orderData.id}&id=${newAppointmentId}&cartId=${cartItemId}`;
    } else {
      window.location.href = `${redirectUrl}?redirect=${window.location.origin}/personale/add-to-cart&id=${newAppointmentId}&cartId=${cartItemId}`;
    }
  };

  /* Document functions */
  const updateFileLinkedField = (
    filePath: string,
    fieldFormName: any,
    checked: boolean
  ) => {
    if (![true, false].includes(checked)) return;

    const allFiles = [...uploadedFiles];
    const self = allFiles.find((file: any) => file.path === filePath);
    self.linkedField = checked ? fieldFormName : null;

    setUploadedFiles(allFiles);
  };

  const getClusterIndex = (field: any) => {
    const dataList = field.formName.split("-");
    if (!dataList?.length) return;

    const [typeName, itemIndex] = [dataList[2], dataList[5]];

    if (!["inputFieldsClusters", "outputFieldsClusters"].includes(typeName))
      return null;

    return +itemIndex + 1;
  };

  const deleteFile = (path: string) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.path !== path));
  };

  const checkIsFieldLinked = (fieldFormName: string) => {
    const allFiles = [...uploadedFiles];
    const self = allFiles.find(
      (file: any) => file.linkedField === fieldFormName
    );

    return self?.linkedField === fieldFormName;
  };

  const fetchOutputDocs = async (fields: any) => {
    const allDocs: any[] = [];
    for (const field of fields) {
      if (!field.documents?.length) continue;

      const signedUrl = await getSignedUrl(field.documents[0]);
      allDocs.push({
        isOutput: field.ioType === "output",
        path: signedUrl,
        preview: signedUrl,
        imageUrl: signedUrl,
        originalUrl: field.documents[0],
        linkedField: field.formName,
      });

      setValue(field.formName, field.documents[0]);
    }

    setUploadedFiles(allDocs);
  };

  /* Hooks */
  useEffect(() => {
    const orderedServices: any[] = [];
    const appointmentUUIDs: any[] = [];
    const refIds: string[] = [];

    // Fetching master order details
    if (masterId) {
      getMasterOrderDetails({
        variables: {id: parseInt(masterId)},
      });
    }

    // Format data stored in local storage
    if (appointmentId) {
      const allServices = props.data.map((item: any) => item.service).flat();

      const data = JSON.parse(localStorage.getItem(appointmentId) || "[]");
      if (!data?.length) return;

      data.forEach((item: any) => {
        const service = allServices.find(
          (serv: any) => serv.id === item.serviceId
        );
        if (!service) return;

        const itemFields = getFieldsForOldOrder(item.fields, service);

        itemFields.forEach((field: any) => {
          if (field.type === "appointment" && field.value === appointmentId) {
            field.value = field.value + "_" + refId;
            field.refId = refId; // Replace the value with refId
          }

          if (
            field.type === "appointmentPatente" &&
            field.value === appointmentId
          ) {
            field.value = field.value + "_" + refId;
            field.refId = refId; // Replace the value with refId
          }
        });

        const resp = formatFieldsIntoObjects(itemFields, service);

        orderedServices.push({
          ...resp,
          id: item.id,
          service,
        });

        // Grabbing IDs of all appointments
        for (const field of itemFields) {
          if (
            (field.type === "appointment" ||
              field.type === "appointmentPatente") &&
            field.refId &&
            !refIds.includes(field.refId)
          ) {
            refIds.push(field.refId);
          }

          if (
            (field.type === "appointment" ||
              field.type === "appointmentPatente") &&
            field.value &&
            !appointmentUUIDs.includes(field.value)
          ) {
            appointmentUUIDs.push(field.value);
          }
        }
      });
    }
    // Format data of provided order details
    else {
      props.data.forEach((item: any) => {
        const itemFields = props.isOutputType
          ? getFieldsForOldOrder(item.fields, item.service)
          : item.service.fields;

        const resp = formatFieldsIntoObjects(itemFields, item.service);

        orderedServices.push({
          ...resp,
          id: item.id,
          service: item.service,
        });

        // Grabbing IDs of all appointments
        for (const field of itemFields) {
          if (
            (field.type === "appointment" ||
              field.type === "appointmentPatente") &&
            field.value &&
            !appointmentUUIDs.includes(field.value)
          ) {
            appointmentUUIDs.push(field.value);
          }
        }
      });
    }

    setValue("orderedServices", orderedServices);

    // Opening chat based on url search params
    if (uChat === "true") setIsChatActive(true);

    const splittedAppointmentRefIds = appointmentUUIDs.map((uuid: string) => {
      return uuid.split("_")[1];
    });

    const finalRefIds = Array.from(
      new Set([...splittedAppointmentRefIds, ...refIds])
    );
    // Fetching details of all appointments using refId
    const timer = setTimeout(
      async () => {
        if (!finalRefIds.length) return;

        for (let i = 0; i < finalRefIds.length; i++) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Add a 1000ms delay between each call
          getAppointmentByRefId({variables: {refId: +finalRefIds[i]}});
        }
      },
      appointmentId ? 3000 : 10
    );

    return () => clearTimeout(timer);

    // Fetching details of all appointments using refId=
  }, [props.data, refId, appointmentId]);

  useEffect(() => {
    if (!formValues) return;

    const toSet: any[] = [];

    formValues.forEach((orderedS: any, i: number) => {
      orderedS.inputFields.forEach((field: any, j: number) => {
        if (field.type === "file") {
          toSet.push({
            ...field,
            formName: `documents.orderedServices-${i}-inputFields-${j}`,
            service: orderedS.service,
          });
        }
      });

      orderedS.outputFields.forEach((field: any, j: number) => {
        if (field.type === "file") {
          toSet.push({
            ...field,
            formName: `documents.orderedServices-${i}-outputFields-${j}`,
            service: orderedS.service,
          });
        }
      });

      orderedS.inputFieldsClusters.forEach((cluster: any, j: number) => {
        cluster.items.forEach((item: any, k: number) => {
          item.fields.forEach((field: any, l: number) => {
            if (field.type === "file") {
              toSet.push({
                ...field,
                formName: `documents.orderedServices-${i}-inputFieldsClusters-${j}-items-${k}-fields-${l}`,
                service: orderedS.service,
              });
            }
          });
        });
      });

      orderedS.outputFieldsClusters.forEach((cluster: any, j: number) => {
        cluster.items.forEach((item: any, k: number) => {
          item.fields.forEach((field: any, l: number) => {
            if (field.type === "file") {
              toSet.push({
                ...field,
                formName: `documents.orderedServices-${i}-outputFieldsClusters-${j}-items-${k}-fields-${l}`,
                service: orderedS.service,
              });
            }
          });
        });
      });
    });

    if (uploadedFiles.length < 1) fetchOutputDocs(toSet);
    if (fileFields.length !== toSet.length) setFileFields(toSet);
  }, [formValues]);

  //
  useEffect(() => {
    if (props.orderData?.accountHolder) {
      setValue("accountHolder", props.orderData?.accountHolder);
    }
  }, [props.orderData]);

  //
  useEffect(() => {
    if (!refId) return;
  }, [refId]);

  //
  return (
    <div>
      <form onSubmit={handleSubmit(submitOrderData)}>
        <IonGrid>
          <IonRow>
            {/* Input Fields */}
            <IonCol size="12" sizeSm="7">
              {fields.map((service: any, index) => (
                <div key={index} style={{marginBottom: "2rem"}}>
                  <SingleOrder
                    data={service}
                    isOutputType={props.isOutputType}
                    name={`orderedServices[${index}]`}
                    redirectToAppointmentPage={redirectToAppointmentPage}
                    formProps={formProps}
                    allAppointments={appointments}
                    allRefIds={refIds}
                    removeServiceFromCart={removeServiceFromCart}
                    note={service.service.description || props.note}
                    secondaryNote={
                      service.service.secondaryNote || props.secondaryNote
                    }
                    refId={refId || undefined}
                  />
                </div>
              ))}

              <Accordion title="Intestatario pratica">
                <Input
                  name=""
                  registerName="accountHolder"
                  register={register}
                  errors={errors}
                  requiredMessage="Denominazione società / nominativo persona fisica è obbligatorio"
                />
              </Accordion>
            </IonCol>

            {/* Documents */}
            <IonCol size="12" size-sm="5">
              <div className="attesa-service-document-section">
                <p className="attesa-service-font">
                  Documenti necessari (pdf-.doc-xblr - jpeg):
                </p>

                <div className="attesa-service-document">
                  {(props.isOutputType ? fileFields : inputFileFields)?.filter(
                    (field) => !field.isHidden
                  ).length > 0 && (
                    <>
                      {/* Input documents */}
                      <div>
                        {inputFileFields.filter((field) => !field.isHidden) && (
                          <div className="document-group-box">
                            <div className="document-by-title">
                              Documenti richiesti
                            </div>
                            {inputFileFields.map((field, index) => (
                              <DocumentItem
                                key={index}
                                index={index}
                                field={field}
                                clusterIndex={getClusterIndex(field)}
                                showServiceName={fields.length > 1}
                                checkIsFieldLinked={checkIsFieldLinked}
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Output documents */}
                      {props.isOutputType &&
                        outputFileFields.filter((field) => !field.isHidden)
                          .length > 0 && (
                          <div className="document-group-box">
                            <div className="document-by-title">
                              Documenti ricevuti da Cosmilano
                            </div>
                            {outputFileFields.map((field, index) => (
                              <DocumentItem
                                key={index}
                                index={index}
                                field={field}
                                clusterIndex={getClusterIndex(field)}
                                showServiceName={fields.length > 1}
                                checkIsFieldLinked={checkIsFieldLinked}
                              />
                            ))}
                          </div>
                        )}

                      {/* Drag and Drop container */}
                      <div className="attesa-dnd-container">
                        {uploadedFiles.length > 0 && (
                          <div
                            className="attesa-button-file-link attesa-button-font"
                            onClick={() => setIsFileModalOpen(true)}
                          >
                            Vedi file caricati
                          </div>
                        )}

                        <div className="attesa-service-drag-and-drop">
                          <div className="attesa-dnd-border">
                            <div
                              {...getRootProps()}
                              className="attesa-full-block"
                            >
                              <input
                                {...getInputProps()}
                                className="attesa-full-block"
                              />
                              <FileSvg />
                              <p>Sfoglia o trascina qui i tuoi file</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {(props.isOutputType ? fileFields : inputFileFields)?.filter(
                    (field) => !field.isHidden
                  ).length < 1 && (
                    <div className="attesa-service-no-document">
                      Nessun documento richiesto!
                    </div>
                  )}
                </div>

                <div className="attesa-service-document-button">
                  {props?.orderData?.roomId && (
                    <div style={{marginRight: "1rem"}}>
                      <ChatIcon
                        roomId={props.orderData?.roomId}
                        id={props.orderData?.id}
                        category="order"
                        isModalActive={isChatActive}
                        setIsModalActive={setIsChatActive}
                        orderNumber={props.orderData?.orderNumber}
                      />
                    </div>
                  )}

                  <button
                    className="attesa-confirm-button attesa-button-font"
                    type="submit"
                  >
                    {props.isOutputType ? "Salva" : "Prosegui"}
                  </button>
                </div>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>

        <AppButton
          onClick={() => {
            const searchBarInput = document.getElementById(
              "header-search-input"
            );

            if (searchBarInput) {
              searchBarInput?.click();
            }
          }}
        >
          Add Servizio
        </AppButton>

        <FileLink
          visible={isFileModalOpen}
          dismiss={() => setIsFileModalOpen(false)}
          files={uploadedFiles}
          fileFields={fileFields}
          setFileFields={() => setFileFields}
          updateFileLinkedField={updateFileLinkedField}
          deleteFile={deleteFile}
          detailed={props.isOutputType}
        />
      </form>
    </div>
  );
};

//
const SingleOrder = (props: ISingleOrderProps) => {
  const isOutputFieldsVisible = props.isOutputType ?? false;

  const getUrgencyMessage = () => {
    const criticalDeadline = props.data?.service?.criticalDeadline;
    if (!criticalDeadline) return undefined;
    if (criticalDeadline.length < 1) return undefined;

    const dateValues = props.data.inputFields
      .filter((field: any) =>
        ["Data Scadenza", "richiesta di evasione entro il"].includes(field.name)
      )
      .map((field: any) => field.value)
      .filter((value: any) => value);

    if (!dateValues.length) return undefined;

    const earlierDate = dateValues.reduce((prev: any, curr: any) => {
      return prev < curr ? prev : curr;
    });

    const diff = Math.abs(dayjs(new Date()).diff(earlierDate));
    const daysDiff = Math.ceil(diff / 1000 / 60 / 60 / 24);

    const charge = criticalDeadline?.find((deadline: any) => {
      return daysDiff >= deadline.from && daysDiff <= deadline.to;
    });
    if (!charge) return undefined;

    return `Diritto di urgenza ${charge.value} euro (per ${daysDiff} gg)`;
  };

  return (
    <div>
      <div
        className="attesa-service-title attesa-service-font"
        style={{
          marginBottom: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{props.data.service.name}</span>
        <p
          className="cart-remove-icon-button"
          style={{
            background: "transparent",
            color: "black",
          }}
          onClick={() => {
            props.removeServiceFromCart(props.data.id);
          }}
        >
          X
        </p>
      </div>

      <div className="attesa-service-inputs-section">
        <strong>Note pratica</strong>
        <div>{props.note ?? "-"}</div>

        <br />

        <strong style={{marginTop: "1rem"}}>Note Pubbliche</strong>
        <div>{props.secondaryNote ?? "-"}</div>

        {isOutputFieldsVisible && <p>Dati richiesti</p>}

        {/* Input Fields */}
        <ClusterItemField
          formProps={props.formProps}
          name={`${props.name}.inputFields`}
          allValues={props.data.inputFields}
          getUrgencyMessage={getUrgencyMessage}
          allAppointments={props.allAppointments}
          allRefIds={props.allRefIds}
          redirectToAppointmentPage={props.redirectToAppointmentPage}
          cartItemId={props.data.id}
        />

        {/* Input Clusters */}
        {props.data.inputFieldsClusters.length > 0 && (
          <ClusterFieldArray
            formProps={props.formProps}
            name={`${props.name}.inputFieldsClusters`}
            allValues={props.data.inputFieldsClusters}
            isModifiable={true}
            cartItemId={props.data.id}
          />
        )}
      </div>

      <div
        style={{
          height: isOutputFieldsVisible ? "auto" : "1px",
          overflow: isOutputFieldsVisible ? "visible" : "hidden",
        }}
        className="attesa-service-inputs-section"
      >
        <IonItemDivider style={{marginTop: "1rem"}} />

        {isOutputFieldsVisible && <p>Dati ricevuti da Cosmilano</p>}

        {/* Output Fields */}
        <ClusterItemField
          formProps={props.formProps}
          name={`${props.name}.outputFields`}
          allValues={props.data.outputFields}
          allAppointments={props.allAppointments}
          allRefIds={props.allRefIds}
          cartItemId={props.data.id}
        />

        {/* Output Clusters */}
        <ClusterFieldArray
          formProps={props.formProps}
          name={`${props.name}.outputFieldsClusters`}
          allValues={props.data.outputFieldsClusters}
          isModifiable={false}
          cartItemId={props.data.id}
        />
      </div>
    </div>
  );
};

const ClusterFieldArray = (props: any) => {
  const {fields} = useFieldArray({
    control: props.formProps.control,
    name: props.name,
  });

  return (
    <>
      {fields.map((_field: any, index) => (
        <div key={index}>
          <ClusterItemFieldArray
            formProps={props.formProps}
            name={`${props.name}[${index}].items`}
            allValues={props.allValues}
            isModifiable={props.isModifiable}
          />
        </div>
      ))}
    </>
  );
};

const ClusterItemFieldArray = (props: any) => {
  const {fields, append, remove} = useFieldArray({
    control: props.formProps.control,
    name: props.name,
  });

  const clusterIndex = props.name.split("[")?.pop()?.split("]")?.[0];
  const clusterName = props.allValues?.[clusterIndex]?.name ?? "";

  const addClusterItemField = () => {
    append({
      fields: (fields[0] as any).fields.map((field: any) => ({
        ...field,
        value: null,
      })),
    });
  };

  return (
    <>
      {fields.map((_field, index) => (
        <div key={index}>
          <div style={{display: "flex", alignItems: "center"}}>
            <h5>
              <strong>
                <span>{clusterName}</span>
                <span> </span>
                <span>{index + 1}</span>
              </strong>
            </h5>

            {fields.length > 1 && props.isModifiable && (
              <div
                style={{marginLeft: "1rem"}}
                className="button-cluster button-cluster-remove"
                onClick={() => remove(index)}
              >
                rimuovi
              </div>
            )}
          </div>
          <ClusterItemField
            formProps={props.formProps}
            name={`${props.name}[${index}].fields`}
            allValues={props.allValues}
          />

          <IonItemDivider style={{marginBottom: "1rem"}} />
        </div>
      ))}

      {props.isModifiable && (
        <>
          <div>
            <div
              className="button-cluster button-cluster-confirm"
              onClick={() => addClusterItemField()}
            >
              <span>Aggiungi</span>
              {' "'}
              <strong>{clusterName}</strong>
              {'"'}
            </div>
          </div>
          <IonItemDivider style={{marginBottom: "2rem", paddingTop: 0}} />
        </>
      )}
    </>
  );
};

const ClusterItemField = (props: any) => {
  const fields = useWatch({
    control: props.formProps.control,
    name: props.name,
  });

  //
  return (
    <IonRow>
      {fields.map((field: any, index: number) => (
        <IonCol
          key={index}
          style={{
            width: field.isHidden ? "0px" : "auto",
            height: field.isHidden ? "0px" : "auto",
            overflow: field.isHidden ? "hidden" : "visible",
          }}
          size={field.isHidden || field.type === "file" ? "0" : "12"}
        >
          <SingleField
            field={field}
            registerName={`${props.name}[${index}].value`}
            formProps={props.formProps}
            getUrgencyMessage={props.getUrgencyMessage}
            redirectToAppointmentPage={props.redirectToAppointmentPage}
            allAppointments={props.allAppointments ?? []}
            allRefIds={props.allRefIds}
            {...props}
          />
        </IonCol>
      ))}
    </IonRow>
  );
};

const SingleField = (props: ISingleFieldProps) => {
  const {field, registerName, formProps} = props;

  const isOutputType = field.ioType === "output";

  const [appointmentItemDetails, setAppointmentItemDetails] =
    useState<any>(null);

  const changeStringToRegex = (value: string): any => {
    if (value) {
      const str = value;
      const startSlash = str.startsWith("/") ? "" : "/";
      const endSlash = str.endsWith("/i")
        ? ""
        : str.endsWith("/g")
        ? ""
        : str.endsWith("/gi")
        ? ""
        : str.endsWith("/")
        ? ""
        : "/";

      const finalValue = `${startSlash}${str}${endSlash}`;
      const lastSlash = finalValue.lastIndexOf("/");
      const restoredRegex = new RegExp(
        finalValue.slice(1, lastSlash),
        finalValue.slice(lastSlash + 1)
      );

      return new RegExp(restoredRegex);
    } else {
      return undefined;
    }
  };

  const getAppointmentStatus = (status: string) => {
    switch (status) {
      case "approved":
        return "Approvato";

      case "canceled":
        return "Cancellato";

      case "pending":
        return "In attesa di";

      case "refused":
        return "Rifiutato";

      default:
        return "";
    }
  };

  useEffect(() => {
    if (field.type === "appointment" || field.type === "appointmentPatente") {
      const allValues = formProps.getValues();

      const [osName, fieldName] = registerName.split(".");
      const osIndex = osName.split("[")[1].split("]")[0];
      const fieldType = fieldName.split("[")[0];
      const fieldIndex = fieldName.split("[")[1].split("]")[0];

      const appointUuid =
        allValues["orderedServices"]?.[osIndex]?.[fieldType]?.[fieldIndex]
          ?.value;

      const onlyAppointRefId = appointUuid?.split("_")[1];

      const appointmentItem = props.allRefIds.find(
        (appointment: any) => appointment.refId === +onlyAppointRefId
      );

      if (appointmentItem) setAppointmentItemDetails(appointmentItem);
    } else return;
  }, [props.allRefIds, field]);

  return (
    <div>
      {/* Date type input */}
      {field.type === "date" && (
        <DatePicker
          control={formProps.control}
          name={capitalizeString(field.name)}
          max={dayjs().add(99, "year").format("YYYY-MM-DD")}
          min={
            field.name === "Data Scadenza" ||
            field.name === "richiesta di evasione entro il"
              ? dayjs().add(1, "day").format("YYYY-MM-DD")
              : undefined
          }
          registerName={registerName}
          titleError={props.getUrgencyMessage?.()}
          register={formProps.register}
          errors={formProps.errors}
          requiredMessage={
            field.isRequired && !field.isHidden
              ? `${field.name} e obbligatario`
              : undefined
          }
          disabled={isOutputType}
        />
      )}
      {/* Select type input */}
      {field.type === "fieldList" && (
        <Select
          name={field.name}
          registerName={registerName}
          register={formProps.register}
          errors={formProps.errors}
          options={field.multipleInputFieldName.map((itm: any) => ({
            value: itm,
            display: itm,
          }))}
          requiredMessage={
            field.isRequired && !field.isHidden
              ? `${field.name} e obbligatario`
              : undefined
          }
          disabled={isOutputType}
        />
      )}
      {/* Text type input */}
      {["text", "regexp", "readonly"].includes(field.type) && (
        <Input
          name={field.name}
          registerName={registerName}
          register={formProps.register}
          errors={formProps.errors}
          requiredMessage={
            field.isRequired && !field.isHidden
              ? `${field.name} e obbligatario`
              : undefined
          }
          pattern={
            field.type === "regexp" && field.inputFieldName
              ? {
                  value: changeStringToRegex(field.inputFieldName),
                  message: "Formato non corretto",
                }
              : undefined
          }
          disabled={isOutputType}
        />
      )}
      {/* Appointment type input */}
      {(field.type === "appointment" ||
        field.type === "appointmentPatente") && (
        <>
          {appointmentItemDetails && (
            <div
              style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "16px",
                backgroundColor: "#f9f9f9",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h3
                style={{
                  marginBottom: "12px",
                  color: "#333",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {field.name}
              </h3>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <div>
                  <strong>Status:</strong>{" "}
                  <span
                    style={{
                      color:
                        appointmentItemDetails.status === "approved"
                          ? "green"
                          : appointmentItemDetails.status === "canceled"
                          ? "red"
                          : "orange",
                    }}
                  >
                    {getAppointmentStatus(appointmentItemDetails.status)}
                  </span>
                </div>
                <div>
                  <strong>Reference ID:</strong> {appointmentItemDetails.refId}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <div>
                  <strong>Booking Start:</strong>{" "}
                  {dayjs(appointmentItemDetails.bookingStart).format(
                    "DD-MM-YYYY hh:mm A"
                  )}
                </div>
                <div>
                  <strong>Booking End:</strong>{" "}
                  {dayjs(appointmentItemDetails.bookingEnd).format(
                    "DD-MM-YYYY hh:mm A"
                  )}
                </div>
              </div>

              <div>
                <strong>Customer Email:</strong>{" "}
                {appointmentItemDetails.customerEmail.join(", ")}
              </div>
            </div>
          )}
          {!appointmentItemDetails && (
            <Input
              name={field.name}
              registerName={registerName}
              register={formProps.register}
              errors={formProps.errors}
              requiredMessage={
                field.isRequired && !field.isHidden
                  ? `${field.name} e obbligatario`
                  : undefined
              }
              hidden
            >
              <div
                className="attesa-button-appointment attesa-button-font"
                onClick={() => {
                  if (!props.cartItemId) return;
                  props.redirectToAppointmentPage?.(
                    registerName,
                    props.cartItemId,
                    field.type
                  );
                }}
              >
                prendere appuntamento
                {appointmentItemDetails}
              </div>
            </Input>
          )}
        </>
      )}
    </div>
  );
};

const DocumentItem = (props: any) => {
  return (
    <div className={"attesa-service-document-file mukta-normal-500-16"}>
      <div>
        <span>{(props.index + 1).toString().padStart(2, "0")}</span>
        <span>{" - "}</span>
        <span>{props.field.name}</span>

        {props.showServiceName && (
          <div style={{display: "inline"}}>
            <span>{" ("}</span>
            <span>
              <i>{props?.field?.service?.name}</i>
            </span>
            <span>{") "}</span>
          </div>
        )}

        {props.clusterIndex && (
          <div style={{display: "inline"}}>
            <span>{" - "}</span>
            <strong>
              {props.field.cluster} {props.clusterIndex}
            </strong>
          </div>
        )}
      </div>

      <div>
        {props.checkIsFieldLinked(props.field.formName) && <CheckSvg />}
      </div>
    </div>
  );
};

const FileSvg = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M54.8438 60C57.6914 60 60 57.6914 60 54.8438V42.6562C60 39.8086 57.6914 37.5 54.8438 37.5H43.1953L49.8047 30.8906C52.7461 27.9375 50.6602 22.8867 46.4883 22.8867H40.3125V4.6875C40.3125 2.09766 38.2148 0 35.625 0H24.375C21.7852 0 19.6875 2.09766 19.6875 4.6875V22.8867H13.5117C9.33984 22.8867 7.24219 27.9375 10.1953 30.8906L16.8047 37.5H5.15625C2.30859 37.5 0 39.8086 0 42.6562V54.8438C0 57.6914 2.30859 60 5.15625 60H54.8438ZM43.125 48.2812C41.8359 48.2812 40.7812 49.3359 40.7812 50.625C40.7812 51.9141 41.8359 52.9688 43.125 52.9688C44.4141 52.9688 45.4688 51.9141 45.4688 50.625C45.4688 49.3359 44.4141 48.2812 43.125 48.2812ZM52.9688 50.625C52.9688 51.9141 51.9141 52.9688 50.625 52.9688C49.3359 52.9688 48.2812 51.9141 48.2812 50.625C48.2812 49.3359 49.3359 48.2812 50.625 48.2812C51.9141 48.2812 52.9688 49.3359 52.9688 50.625ZM29.332 44.7305C29.6953 45.0938 30.293 45.0938 30.6562 44.7305L47.1445 28.2422C47.7305 27.6562 47.3203 26.6367 46.4766 26.6367H36.5625V4.6875C36.5625 4.17188 36.1406 3.75 35.625 3.75H24.375C23.8594 3.75 23.4375 4.17188 23.4375 4.6875V26.6367H13.5117C12.6797 26.6367 12.2578 27.6445 12.8438 28.2422L29.332 44.7305ZM54.8438 41.25C55.6172 41.25 56.25 41.8828 56.25 42.6562V54.8438C56.25 55.6172 55.6172 56.25 54.8438 56.25H5.15625C4.38281 56.25 3.75 55.6172 3.75 54.8438V42.6562C3.75 41.8828 4.38281 41.25 5.15625 41.25H20.5547L26.6836 47.3789C28.5117 49.207 31.4883 49.207 33.3164 47.3789L39.4453 41.25H54.8438Z"
        fill="#588CD3"
      />
    </svg>
  );
};

const CheckSvg = () => {
  return (
    <svg
      width="20"
      height="15"
      viewBox="0 0 20 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.79289 14.7054L0.292881 8.16802C-0.0976271 7.77527 -0.0976271 7.13846 0.292881 6.74567L1.70706 5.32332C2.09757 4.93053 2.73077 4.93053 3.12128 5.32332L7.5 9.72717L16.8787 0.294565C17.2692 -0.0981884 17.9024 -0.0981884 18.2929 0.294565L19.7071 1.71692C20.0976 2.10967 20.0976 2.74648 19.7071 3.13927L8.20711 14.7054C7.81656 15.0982 7.1834 15.0982 6.79289 14.7054Z"
        fill="#85CB28"
      />
    </svg>
  );
};

export default Orders;

interface IOrdersProps {
  data: any[];
  orderData?: any;
  existingOrder?: any;
  isOutputType?: boolean;
  note?: string;
  secondaryNote?: string;

  handleGoToNextPage?: (formFieldData: any) => void;
}

interface ISingleOrderProps {
  data: any;
  name: string;
  formProps: any;
  isOutputType?: boolean;
  allAppointments: any[];
  allRefIds: any[];
  redirectToAppointmentPage: (
    fieldName: string,
    cartItemId: number,
    appointmentType: "appointment" | "appointmentPatente"
  ) => void;
  removeServiceFromCart: (serviceId: number) => void;
  note?: string;
  secondaryNote?: string;
  refId?: string;
}

interface ISingleFieldProps {
  field: any;
  formProps: any;
  registerName: string;
  allAppointments: any[];
  allRefIds: any[];
  getUrgencyMessage?: () => string | undefined;
  redirectToAppointmentPage?: (
    fieldName: string,
    cartItemId: number,
    appointmentType: "appointment" | "appointmentPatente"
  ) => void;
  cartItemId?: number;
}
